import React, { useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './PoojaFilters.css';

const FILTER_SECTIONS = [
  { key: 'deity', label: 'Deity', icon: 'fas fa-om' },
  { key: 'tithis', label: 'Tithis', icon: 'fas fa-moon' },
  { key: 'dosha', label: 'Dosha', icon: 'fas fa-eye' },
  { key: 'benefits', label: 'Benefits', icon: 'fas fa-hand-holding-heart' },
  { key: 'location', label: 'Location', icon: 'fas fa-location-dot' }
];

const DEITY_IMAGE_MAP = {
  vishnu: '/images/deities/vishnu.webp',
  shiva: '/images/deities/shiva.webp',
  rudra: '/images/deities/shiva.webp',
  ganesha: '/images/deities/ganesha.webp',
  ganesh: '/images/deities/ganesha.webp',
  lakshmi: '/images/deities/lakshmi.webp',
  mahalakshmi: '/images/deities/lakshmi.webp',
  kubera: '/images/deities/kubera.webp',
  durga: '/images/deities/durga.webp',
  kali: '/images/deities/kali.webp',
  hanuman: '/images/deities/hanuman.webp',
  saraswati: '/images/deities/saraswati.webp',
  parvati: '/images/deities/parvati.webp',
  katyayani: '/images/deities/parvati.webp',
  shani: '/images/deities/shani.webp',
  bagalamukhi: '/images/deities/bagalamukhi.webp',
  navgraha: '/images/deities/navgraha.webp',
  guru: '/images/deities/guru.webp',
  'vastu purusha': '/images/deities/vastu_purusha.webp'
};

const normalizeValue = (value = '') => String(value).toLowerCase().trim();

const getDeityImageByName = (deityName = '') => {
  const normalized = normalizeValue(deityName);
  if (DEITY_IMAGE_MAP[normalized]) return DEITY_IMAGE_MAP[normalized];

  const partialKey = Object.keys(DEITY_IMAGE_MAP).find((key) =>
    normalized.includes(key) || key.includes(normalized)
  );
  return partialKey ? DEITY_IMAGE_MAP[partialKey] : null;
};

const dedupeAndSort = (values = []) => {
  const unique = Array.from(new Set(values.filter(Boolean)));
  return unique.sort((a, b) => a.localeCompare(b));
};

const getFilterOptions = (poojaItems = []) => {
  const optionsBySection = {
    deity: [],
    tithis: [],
    dosha: [],
    benefits: [],
    location: []
  };

  poojaItems.forEach((pooja) => {
    const filters = pooja?.filters || {};
    FILTER_SECTIONS.forEach(({ key }) => {
      const sectionValues = Array.isArray(filters[key]) ? filters[key] : [];
      sectionValues.forEach((entry) => optionsBySection[key].push(entry));
    });
  });

  Object.keys(optionsBySection).forEach((key) => {
    optionsBySection[key] = dedupeAndSort(optionsBySection[key]);
  });

  return optionsBySection;
};

const createEmptySelections = () => ({
  deity: [],
  tithis: [],
  dosha: [],
  benefits: [],
  location: []
});

const PoojaFilters = ({ poojaItems = [], appliedSelections, onApply }) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('deity');
  const [draftSelections, setDraftSelections] = useState(appliedSelections || createEmptySelections());

  const optionsBySection = useMemo(() => getFilterOptions(poojaItems), [poojaItems]);

  const selectedCount = useMemo(() => (
    Object.values(appliedSelections || {}).reduce((total, values) => total + (values?.length || 0), 0)
  ), [appliedSelections]);

  const openDrawer = () => {
    setDraftSelections(appliedSelections || createEmptySelections());
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
  };

  const isSelected = (sectionKey, value) => {
    const normalized = normalizeValue(value);
    return (draftSelections[sectionKey] || []).some((entry) => normalizeValue(entry) === normalized);
  };

  const toggleOption = (sectionKey, value) => {
    setDraftSelections((prev) => {
      const currentValues = prev[sectionKey] || [];
      const normalized = normalizeValue(value);
      const exists = currentValues.some((entry) => normalizeValue(entry) === normalized);

      return {
        ...prev,
        [sectionKey]: exists
          ? currentValues.filter((entry) => normalizeValue(entry) !== normalized)
          : [...currentValues, value]
      };
    });
  };

  const toggleSelectAll = (sectionKey) => {
    const options = optionsBySection[sectionKey] || [];
    if (options.length === 0) return;

    setDraftSelections((prev) => {
      const currentValues = prev[sectionKey] || [];
      const allSelected = options.every((value) =>
        currentValues.some((entry) => normalizeValue(entry) === normalizeValue(value))
      );

      return {
        ...prev,
        [sectionKey]: allSelected ? [] : [...options]
      };
    });
  };

  const clearSelections = () => {
    setDraftSelections(createEmptySelections());
  };

  const applySelections = () => {
    onApply(draftSelections);
    closeDrawer();
  };

  const activeOptions = optionsBySection[activeSection] || [];
  const activeSelectionCount = (draftSelections[activeSection] || []).length;
  const allActiveSelected = activeOptions.length > 0 && activeSelectionCount === activeOptions.length;

  return (
    <div className="pooja-filters-wrapper">
      <div className="pooja-filters-trigger-row">
        <button
          type="button"
          className="pooja-filters-trigger"
          onClick={openDrawer}
          aria-label="Open pooja filters"
        >
          <i className="fas fa-filter" aria-hidden="true"></i>
          <span>{t('onlinePooja.filters.title', {}, 'Pooja Filters')}</span>
          {selectedCount > 0 && <span className="pooja-filters-count">{selectedCount}</span>}
        </button>

        {selectedCount > 0 && (
          <button
            type="button"
            className="pooja-filters-inline-clear"
            onClick={() => onApply(createEmptySelections())}
          >
            {t('onlinePooja.filters.clear', {}, 'Clear Filter')}
          </button>
        )}
      </div>

      {isOpen && (
        <>
          <div className="pooja-filters-overlay" onClick={closeDrawer} aria-hidden="true"></div>

          <div className="pooja-filters-drawer" role="dialog" aria-modal="true" aria-label="Pooja Filters Dialog">
            <div className="pooja-filters-header">
              <h3>{t('onlinePooja.filters.title', {}, 'Pooja Filters')}</h3>
              <button type="button" className="pooja-filters-close" onClick={closeDrawer} aria-label="Close filters">
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>

            <div className="pooja-filters-body">
              <aside className="pooja-filter-tabs" aria-label="Filter categories">
                {FILTER_SECTIONS.map((section) => {
                  const sectionCount = (draftSelections[section.key] || []).length;
                  return (
                    <button
                      type="button"
                      key={section.key}
                      className={`pooja-filter-tab ${activeSection === section.key ? 'active' : ''}`}
                      onClick={() => setActiveSection(section.key)}
                    >
                      <i className={section.icon} aria-hidden="true"></i>
                      <span>{section.label}</span>
                      {sectionCount > 0 && <em>{sectionCount}</em>}
                    </button>
                  );
                })}
              </aside>

              <section className="pooja-filter-options" aria-label="Selected filter options">
                <label className="pooja-select-all-row">
                  <input
                    type="checkbox"
                    checked={allActiveSelected}
                    onChange={() => toggleSelectAll(activeSection)}
                  />
                  <span>{t('onlinePooja.filters.selectAll', {}, 'Select All')} ({activeOptions.length})</span>
                </label>

                <div className="pooja-options-list">
                  {activeOptions.map((option) => {
                    const deityImage = activeSection === 'deity' ? getDeityImageByName(option) : null;
                    return (
                      <label key={option} className="pooja-option-row">
                        <input
                          type="checkbox"
                          checked={isSelected(activeSection, option)}
                          onChange={() => toggleOption(activeSection, option)}
                        />
                        <div className="pooja-option-content">
                          {activeSection === 'deity' && (
                            <span className="pooja-deity-thumb-wrap" aria-hidden="true">
                              {deityImage ? (
                                <img
                                  src={deityImage}
                                  alt=""
                                  loading="lazy"
                                  className="pooja-deity-thumb"
                                  onError={(event) => {
                                    event.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <i className="fas fa-om pooja-deity-fallback-icon"></i>
                              )}
                            </span>
                          )}
                          <span>{option}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="pooja-filters-footer">
              <button type="button" className="pooja-filter-clear-btn" onClick={clearSelections}>
                {t('onlinePooja.filters.clear', {}, 'Clear Filter')}
              </button>
              <button type="button" className="pooja-filter-apply-btn" onClick={applySelections}>
                {t('onlinePooja.filters.apply', {}, 'Apply Filter')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PoojaFilters;
